<template>
    <div class="editable-table" :class="{ 'editable-table--stacked': stacked }">

        <!-- Desktop: regular table -->
        <div v-if="!stacked" class="table-responsive mb-0">
            <table class="table table-sm table-touch mb-0" :class="tableClass">
                <thead v-if="!noHeader">
                    <tr>
                        <th v-for="col in columns"
                            :key="col.key"
                            :class="col.class"
                            :style="col.width ? { width: col.width } : null">
                            {{ col.label }}
                        </th>
                        <th v-if="removable" class="editable-table__actions-col"></th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-if="!items.length && emptyText">
                        <td :colspan="columns.length + (removable ? 1 : 0)" class="text-muted text-center py-15">
                            {{ emptyText }}
                        </td>
                    </tr>
                    <tr v-for="(item, index) in items" :key="rowKey(item, index)">
                        <td v-for="col in columns" :key="col.key" :class="col.class">
                            <slot :name="'cell(' + col.key + ')'" :item="item" :index="index" :column="col">
                                {{ item[col.key] }}
                            </slot>
                        </td>
                        <td v-if="removable" class="editable-table__actions-col text-right">
                            <slot name="row-actions" :item="item" :index="index">
                                <b-button variant="flat-danger"
                                          class="btn-icon rounded-circle"
                                          :disabled="disabled"
                                          :title="removeTitle"
                                          @click="$emit('remove', index, item)">
                                    <feather-icon icon="TrashIcon"></feather-icon>
                                </b-button>
                            </slot>
                        </td>
                    </tr>
                </tbody>
                <tfoot v-if="$slots.tfoot">
                    <slot name="tfoot"></slot>
                </tfoot>
            </table>
        </div>

        <!-- Mobile: stacked cards -->
        <div v-else class="card-list">
            <div v-if="!items.length && emptyText" class="text-muted text-center py-15">
                {{ emptyText }}
            </div>
            <div v-for="(item, index) in items" :key="rowKey(item, index)" class="card-list__item">
                <div class="card-list__header">
                    <div class="min-w-0 flex-grow-1">
                        <slot name="title" :item="item" :index="index">
                            <slot :name="'cell(' + titleColumn.key + ')'" :item="item" :index="index" :column="titleColumn">
                                {{ item[titleColumn.key] }}
                            </slot>
                        </slot>
                    </div>
                    <slot v-if="removable" name="row-actions" :item="item" :index="index">
                        <b-button variant="flat-danger"
                                  class="btn-icon rounded-circle tap-target flex-shrink-0"
                                  :disabled="disabled"
                                  :title="removeTitle"
                                  @click="$emit('remove', index, item)">
                            <feather-icon icon="TrashIcon"></feather-icon>
                        </b-button>
                    </slot>
                </div>
                <div v-for="col in bodyColumns" :key="col.key" class="card-list__row" :class="{ 'card-list__row--block': col.block }">
                    <span class="card-list__label">{{ col.label }}</span>
                    <div class="card-list__value">
                        <slot :name="'cell(' + col.key + ')'" :item="item" :index="index" :column="col">
                            {{ item[col.key] }}
                        </slot>
                    </div>
                </div>
            </div>
        </div>

        <div v-if="addLabel || $slots.footer" class="editable-table__footer">
            <slot name="footer">
                <b-button variant="outline-primary" size="sm" :disabled="disabled" @click="$emit('add')">
                    <feather-icon icon="PlusIcon" class="mr-3"></feather-icon>{{ addLabel }}
                </b-button>
            </slot>
        </div>
    </div>
</template>

<script>
    /*
     * Editable "repeater" rows. Renders a compact table on desktop and stacked cards on
     * phones. Cells are provided through scoped slots named cell(<key>):
     *
     *   <EditableTable :items="rows" :columns="[{ key: 'name', label: 'Navn' }, { key: 'qty', label: 'Antal', width: '120px' }]"
     *                  removable add-label="Tilføj" @add="rows.push({})" @remove="i => rows.splice(i, 1)">
     *       <template #cell(name)="{ item }"><b-form-input v-model="item.name" size="sm" /></template>
     *   </EditableTable>
     *
     * columns: [{ key, label, class?, width?, title?: bool (used as the card header on mobile), block?: bool (label above value) }]
     */
    export default {
        props: {
            items: {
                type: Array,
                default: () => []
            },
            columns: {
                type: Array,
                required: true
            },
            keyField: {
                type: String,
                default: null
            },
            removable: {
                type: Boolean,
                default: false
            },
            removeTitle: {
                type: String,
                default: 'Fjern'
            },
            disabled: {
                type: Boolean,
                default: false
            },
            addLabel: {
                type: String,
                default: null
            },
            emptyText: {
                type: String,
                default: null
            },
            noHeader: {
                type: Boolean,
                default: false
            },
            tableClass: {
                type: [String, Array, Object],
                default: null
            },
            // 'md' → cards below 768px, 'lg' → cards below 992px, 'none' → always table
            stackedBelow: {
                type: String,
                default: 'md'
            },
            // explicit override of the breakpoint logic (e.g. from a container-width measurement)
            forceStacked: {
                type: Boolean,
                default: null
            }
        },
        computed: {
            stacked() {
                if (this.forceStacked !== null) return this.forceStacked
                if (this.stackedBelow === 'lg') return this.isTablet
                if (this.stackedBelow === 'sm') return this.isPhone
                if (this.stackedBelow === 'none') return false
                return this.isMobile
            },
            titleColumn() {
                return this.columns.find(x => x.title) || this.columns[0]
            },
            bodyColumns() {
                return this.columns.filter(x => x.key !== this.titleColumn.key)
            }
        },
        methods: {
            rowKey(item, index) {
                if (this.keyField && item && item[this.keyField] !== undefined && item[this.keyField] !== null) {
                    return item[this.keyField]
                }
                return index
            }
        }
    }
</script>

<style lang="scss" scoped>
    .editable-table__actions-col {
        width: 52px;
        white-space: nowrap;
    }

    .editable-table__footer {
        padding-top: 10px;
    }

    .card-list__value {
        flex: 1 1 auto;
        min-width: 0;
        text-align: right;

        ::v-deep .form-control,
        ::v-deep .v-select,
        ::v-deep .input-group {
            text-align: left;
        }
    }

    .card-list__row--block {
        flex-direction: column;
        align-items: stretch;

        .card-list__value {
            text-align: left;
        }
    }
</style>
